/**
 * Login Page
 */

// Core imports
import React from 'react';
import PropTypes from 'prop-types';

// Style, SEO and settings
import Helmet from 'react-helmet';
import {
  Col,
  Grid,
  Row,
  styled,
  withTheme,
  thd,
  th,
  up,
  css,
} from '@smooth-ui/core-sc';

// Additional Components/Containers
import LoadAsync from '_platform/src/utils/LoadAsync';
import PublicComponent from '_platform/src/utils/PublicComponent';
import getReturnUrlPath from '_platform/src/utils/getReturnUrlPath';

import { withSettings } from 'containers/WebApp/SettingsContext';
import bgImage1x from './images/LoginPage-BG.jpg';
import bgImage2x from './images/LoginPage-BG@2x.jpg';
import logo from './images/Logo@2x.png';

const LoginProvider = LoadAsync(() =>
  import(
    /* webpackChunkName: "loginProvider" */ '_platform/src/containers/LoginProvider/LoginProvider'
  )
);
const LoginForm = LoadAsync(() =>
  import(
    /* webpackChunkName: "loginForm" */ '_platform/src/components/LoginForm/LoginForm'
  )
);

const PageContainer = styled.div`
  align-items: center;
  background-color: #131d28;
  background-image: url(${bgImage1x});
  background-position: center center;
  background-repeat: cover;
  background-size: inherit;
  display: flex;
  flex: 1 0 auto;
  flex-direction: column;
  justify-content: center;
  @media only screen and (min-device-pixel-ratio: 1.5),
    only screen and (min-resolution: 144dpi),
    only screen and (min-resolution: 1.5dppx) {
    background-color: #131d28;
    /* background-image: url(${bgImage2x}); */
  }

  button {
    color: ${thd('secondary', '#fff')};
    font-weight: bold;
    width: 100%;
    border-radius: 24px;
    text-transform: uppercase;
    padding: 10px;
  }
`;

const LoginLogo = styled.h1`
  margin: 0;
  padding: 0;
  z-index: 1;
  position: absolute;
  left: 50%;
  margin-left: -150px;
  top: -150px;
  ${up(
    'md',
    css`
      top: -200px;
      margin-left: -192px;
    `
  )}
  img {
    height: auto;
    width: 300px;
    display: block;
    margin: 0 auto;
    ${up(
      'md',
      css`
        width: 384px;
      `
    )}
  }
`;

const IntroContainer = styled.div`
  margin: 0.5rem 0;

  .sui-row {
    display: flex;
    align-items: flex-end;
  }

  .sui-col:first-child {
    margin-top: 170px;
  }

  label[for='username'],
  label[for='password'] {
    clip: rect(0 0 0 0);
    clip-path: inset(50%);
    height: 1px;
    overflow: hidden;
    position: absolute;
    white-space: nowrap;
    width: 1px;
  }

  input {
    background: #fff !important;
  }
`;

const Card = styled.div`
  box-sizing: border-box;
  background: rgba(240, 240, 240, 0.8);
  border-radius: 24px;
  position: relative;
  padding: 2rem;
  margin-bottom: 20px;
  color: #32393f;

  a {
    color: #32393f;
  }

  &.text--center {
    padding-top: 120px;
    font-size: 18px;
    color: ${th('text')};
  }
`;

const LoginContainer = styled.div`
  input:checked + .sui-checkbox-content {
    background-color: ${th('primary')} !important;
  }

  .reset-password a,
  p {
    font-size: 14px;
    font-weight: normal;
  }
`;

const LoginPage = ({ settings }) => (
  <PublicComponent
    redirectOnError={
      // Redirect to returnUrl, if it's not the same as loginPagePath
      (getReturnUrlPath !== settings.settingsApp.loginPagePath &&
        getReturnUrlPath) ||
      '/'
    }
  >
    <Helmet>
      <title>Login</title>
      {/*<meta name="description" content="Page description" />*/}
    </Helmet>

    <PageContainer className="page-container">
      <Grid>
        <IntroContainer>
          <Grid>
            <Row justifyContent="center">
              <Col xs={12} sm={10} md={8} lg={5}>
                <LoginLogo className="text--center">
                  <img
                    src={logo}
                    alt={
                      settings.settingsApp && settings.settingsApp.siteName
                        ? settings.settingsApp.siteName
                        : 'ProgramName'
                    }
                  />
                </LoginLogo>
                <Card className="text--center">
                  <p>
                    Get ready to say Howdy to Dallas and discover why everything
                    is bigger in Texas and continue on to Montana, the last best
                    place! Introducing your exclusive 2025/26 Distributor Growth
                    Incentive Program!
                  </p>
                  <p>
                    The buying period runs from 3rd November 2025 to 2nd
                    November 2026.
                  </p>
                  <p>
                    <b>Login for more details and to track your progress.</b>
                  </p>
                </Card>
              </Col>
              <Col xs={12} sm={10} md={8} lg={4}>
                <Card>
                  <LoginContainer>
                    <LoginProvider disableMeta>
                      <LoginForm formLabels={{ labelUserName: 'Username' }} />
                    </LoginProvider>
                    <p>
                      If you are having difficulties logging in, please{' '}
                      <a href="mailto:info@yeehaw2yellowstone.com">
                        <b>contact us</b>
                      </a>
                      .
                    </p>
                  </LoginContainer>
                </Card>
              </Col>
            </Row>
          </Grid>
        </IntroContainer>
      </Grid>
    </PageContainer>
  </PublicComponent>
);

LoginPage.propTypes = {
  settings: PropTypes.object.isRequired,
};

export default withTheme(withSettings(LoginPage));
